import {
  ChatMessageTemplate,
  ChatMessageTemplateCreateRequest,
  ChatMessageTemplateOrderBulkUpdateRequest,
  ChatMessageTemplateUpdateRequest,
  MessageTemplateType
} from '../types/message';
import { ApiClient, ApiError } from './apiClient';

export interface ChatMessageTemplateListParams {
  type?: MessageTemplateType;
  category?: string;
  search?: string;
  lang_code?: string;
}

export class ChatMessageTemplateService {
  constructor(private api: ApiClient) { }

  list(params: ChatMessageTemplateListParams = {}) {
    const query: Record<string, string | number | undefined> = {};

    if (params.type !== undefined) {
      query.type = params.type;
    }
    if (params.category !== undefined && params.category !== '') {
      query.category = params.category;
    }
    if (params.search !== undefined && params.search !== '') {
      query.search = params.search;
    }
    if (params.lang_code !== undefined && params.lang_code !== '') {
      query.lang_code = params.lang_code;
    }

    return this.api.get<ChatMessageTemplate[]>(
      '/api/chat-message-templates',
      Object.keys(query).length > 0 ? query : undefined
    ).then(result => (result.success && result.data ? result.data : []));
  }

  getById(id: number, type?: MessageTemplateType, lang_code?: string) {
    const query: Record<string, number | string | undefined> = {};
    if (type !== undefined) {
      query.type = type;
    }
    if (lang_code !== undefined && lang_code !== '') {
      query.lang_code = lang_code;
    }
    return this.api.get<ChatMessageTemplate>(
      `/api/chat-message-templates/${id}`,
      Object.keys(query).length > 0 ? query : undefined
    ).then(result => (result.success && result.data ? result.data : null));
  }

  create(payload: ChatMessageTemplateCreateRequest) {
    return this.api.post<ChatMessageTemplate>(
      '/api/chat-message-templates',
      payload
    ).then(result => (result.success && result.data ? result.data : null));
  }

  update(id: number, payload: ChatMessageTemplateUpdateRequest) {
    return this.api.put<ChatMessageTemplate>(
      `/api/chat-message-templates/${id}`,
      payload
    ).then(result => (result.success && result.data ? result.data : null));
  }

  updateOrder(type: MessageTemplateType, payload: ChatMessageTemplateOrderBulkUpdateRequest) {
    const body = payload.map(item => ({
      id: Number(item.id),
      order: Number(item.order)
    }));
    return this.api.put<{ message: string }>(
      '/api/chat-message-templates/order',
      body,
      { type }
    ).then(result => {
      if (!result.success) {
        throw new ApiError(0, result.error ?? '');
      }
      return result.data ?? { message: '' };
    });
  }

  delete(id: number) {
    return this.api.delete<void>(`/api/chat-message-templates/${id}`).then(() => undefined);
  }

  getQuickCategories(lang_code?: string) {
    const query: Record<string, string | undefined> = {};
    if (lang_code !== undefined && lang_code !== '') {
      query.lang_code = lang_code;
    }
    return this.api.get<{ categories: string[] }>(
      '/api/chat-message-templates/quick/categories',
      Object.keys(query).length > 0 ? query : undefined
    ).then(result => (result.success && result.data ? result.data : { categories: [] }));
  }

  invalidateCache() {
    return this.api.post<{ message: string }>(
      '/api/chat-message-templates/invalidate-cache'
    ).then(result => (result.success && result.data ? result.data : { message: '' }));
  }

  getLangCodes() {
    return this.api.get<{ lang_codes: string[]; default: string }>(
      '/api/chat-message-templates/lang-codes'
    ).then(result => (
      result.success && result.data
        ? result.data
        : { lang_codes: [], default: '' }
    ));
  }

  searchAgent(params: { search?: string; lang_code?: string }) {
    const query: Record<string, string | undefined> = {};

    if (params.search !== undefined && params.search !== '') {
      query.search = params.search;
    }
    if (params.lang_code !== undefined && params.lang_code !== '') {
      query.lang_code = params.lang_code;
    }

    return this.api.get<Array<{ content: string; category: string }>>(
      '/api/chat-message-templates/agent',
      Object.keys(query).length > 0 ? query : undefined
    ).then(result => (result.success && result.data ? result.data : []));
  }
}

